import { DeleteOutlined } from '@ant-design/icons';
import {
  Button, Col, Input, Row, Select, Tooltip,
} from 'antd';
import {
  webengageDealAttributeOptions, WebengageMappingSourceType, webengageMappingSourceTypeOptions,
  webengageMappingUserFieldOptions,
} from 'automations/data/actionNodes';
import { type SendWebengageEventNodeData } from 'automations/types/nodeSchemas/actionNodes';

import styles from './SendWebengageEventContent.module.scss';

type Mapping = SendWebengageEventNodeData['additionalOptions']['sendWebengageEvent']['mappings'][number];

export default function MappingRow({
  mapping,
  onChange,
  onRemove,
  leadAttributeOptions,
  leadAttributeOptionsLoading,
  activityAttributeOptions,
  activityAttributeOptionsLoading,
  activityAttributesAvailable,
  dealAttributesAvailable = false,
}: {
  mapping: Partial<Mapping>;
  onChange: (patch: Partial<Mapping>) => void;
  onRemove?: () => void;
  leadAttributeOptions: { label: string; value: string }[];
  leadAttributeOptionsLoading: boolean;
  activityAttributeOptions: { label: string; value: string }[];
  activityAttributeOptionsLoading: boolean;
  activityAttributesAvailable: boolean;
  dealAttributesAvailable?: boolean;
}) {
  const isActivityAttribute = mapping.sourceType === WebengageMappingSourceType.LEAD_ACTIVITY_ATTRIBUTE;
  const isStatic = mapping.sourceType === WebengageMappingSourceType.STATIC;
  const sourceAttributeOptions = isActivityAttribute ? activityAttributeOptions : leadAttributeOptions;
  const sourceAttributeLoading = isActivityAttribute ? activityAttributeOptionsLoading : leadAttributeOptionsLoading;

  const sourceAttributeDisabledTitle = isActivityAttribute && !activityAttributesAvailable
    ? 'Lead Activity Attributes are available when this automation triggers off a single Activity '
      + '(e.g. Activity Created/Updated)'
    : undefined;

  // A deal source picks one of the deal fields the backend reads off the triggering deal event.
  const isDealAttribute = mapping.sourceType === WebengageMappingSourceType.DEAL_ATTRIBUTE;
  const attributeOptions = isDealAttribute ? webengageDealAttributeOptions : sourceAttributeOptions;
  const attributeDisabledTitle = isDealAttribute && !dealAttributesAvailable
    ? 'Deal Attributes are available when this automation triggers off a Deal (e.g. Deal Created/Updated)'
    : sourceAttributeDisabledTitle;
  // Only a lead or activity field can hold a user, such as Owner.
  const canHoldUser = isActivityAttribute || mapping.sourceType === WebengageMappingSourceType.LEAD_ATTRIBUTE;

  return (
    <Row gutter={16} align="middle" className={styles.mappingRow}>
      <Col span={7}>
        <Input
          placeholder="WebEngage Attribute"
          value={mapping.webengageAttribute}
          onChange={(e) => onChange({ webengageAttribute: e.target.value })}
        />
      </Col>
      <Col span={6}>
        <Select
          className={styles.select}
          placeholder="Source Type"
          options={webengageMappingSourceTypeOptions}
          value={mapping.sourceType}
          onChange={(value) => onChange({
            sourceType: value, sourceAttribute: '', sourceValue: '', userField: undefined,
          })}
        />
      </Col>
      <Col span={7}>
        {isStatic ? (
          <Input
            placeholder="Value"
            value={mapping.sourceValue}
            onChange={(e) => onChange({ sourceValue: e.target.value })}
          />
        ) : (
          <Tooltip title={attributeDisabledTitle}>
            <Select
              className={styles.select}
              placeholder="Source Attribute"
              showSearch
              optionFilterProp="label"
              loading={!isDealAttribute && sourceAttributeLoading}
              disabled={!mapping.sourceType || Boolean(attributeDisabledTitle)}
              options={attributeOptions}
              value={mapping.sourceAttribute || undefined}
              onChange={(value) => onChange({ sourceAttribute: value })}
            />
          </Tooltip>
        )}
      </Col>
      <Col span={4}>
        {onRemove && (
          <Button icon={<DeleteOutlined />} type="text" danger onClick={onRemove}>
            Remove
          </Button>
        )}
      </Col>
      {/* Optional, on a second line under Source Attribute. */}
      {canHoldUser && (
        <Col span={7} offset={13} className={styles.mappingOption}>
          <Tooltip title="For a field that holds a user, such as Owner: send their name or email instead of their id">
            <Select
              className={styles.select}
              placeholder="User field (optional)"
              allowClear
              options={webengageMappingUserFieldOptions}
              value={mapping.userField}
              onChange={(value) => onChange({ userField: value })}
            />
          </Tooltip>
        </Col>
      )}
    </Row>
  );
}
